import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import { API_URL } from '../config/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Clock, Package, Users, User, Phone, AlertCircle, AlertTriangle, Link2, Check, Plus, Lock, Anchor, Star, ChevronDown, ChevronUp, QrCode, Calendar, CalendarDays } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import SignatureCanvas from 'react-signature-canvas';
import MobileWaiverWizard from '../components/MobileWaiverWizard';
import { QRCodeSVG as QRCode } from 'qrcode.react';
import { COUNTRIES } from '../constants/countries';
import { getTimezoneAbbr } from '../utils/timezoneUtils';
import ConversionTracking from '../components/ConversionTracking';

// Helper function to format phone number with country code
const formatPhoneNumber = (phone) => {
  if (!phone) return 'N/A';
  
  // Phone is stored in E.164 format (e.g., +1234567890 or +971501234567)
  const cleaned = phone.replace(/\D/g, '');
  
  // Detect country by phone code prefix
  for (const country of COUNTRIES) {
    const codeDigits = country.phoneCode.replace('+', '');
    if (phone.startsWith(country.phoneCode) || cleaned.startsWith(codeDigits)) {
      const number = cleaned.replace(new RegExp(`^${codeDigits}`), '');
      if (country.code === 'USA' && number.length === 10) {
        return `${country.phoneCode} (${number.substring(0, 3)}) ${number.substring(3, 6)}-${number.substring(6)}`;
      } else if (country.code === 'UAE' && number.length === 9) {
        return `${country.phoneCode} ${number.substring(0, 2)} ${number.substring(2, 5)} ${number.substring(5)}`;
      }
      return `${country.phoneCode} ${number}`;
    }
  }
  
  // Unknown format - return as is
  return phone;
};

// Helper function to format duration smartly
const formatDuration = (minutes) => {
  if (!minutes || minutes <= 0) return 'N/A';
  
  // 60 minutes or less - show in minutes
  if (minutes <= 60) {
    return `${minutes} minutes`;
  }
  
  // Above 60 minutes - calculate hours, days
  const days = Math.floor(minutes / 1440); // 1440 min = 24 hours
  const remainingAfterDays = minutes % 1440;
  const hours = Math.floor(remainingAfterDays / 60);
  const mins = remainingAfterDays % 60;
  
  const parts = [];
  
  if (days > 0) {
    parts.push(days === 1 ? '1 day' : `${days} days`);
  }
  
  if (hours > 0) {
    parts.push(`${hours}hr`);
  }
  
  if (mins > 0) {
    parts.push(`${mins}min`);
  }
  
  return parts.join(' ');
};

const CustomerTicketPage = () => {
  const { ticketId } = useParams();
  const [trip, setBooking] = useState(null);
  const [service, setService] = useState(null); // Add service state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showWaiverModal, setShowWaiverModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [signature, setSignature] = useState('');
  const [signingWaiver, setSigningWaiver] = useState(false);
  const [signaturePad, setSignaturePad] = useState(null);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  const [emergencyAgreed, setEmergencyAgreed] = useState(false);
  const [idImage, setIdImage] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [showAllSignatures, setShowAllSignatures] = useState(false); // For collapsing large signature lists
  const [showQRCode, setShowQRCode] = useState(false); // For QR code modal/section
  const [ticketToken, setTicketToken] = useState(() => {
    // Grab token from URL or fallback to localStorage session
    const urlToken = new URLSearchParams(window.location.search).get('token');
    return urlToken || localStorage.getItem(`ticket_session_${ticketId}`) || '';
  });
  
  // Branding state
  const [orgBranding, setOrgBranding] = useState({
    logoUrl: '',
    primaryColor: '#007AFF',
    name: '',
    showPoweredByBookingFlows: true,
    companyWebsite: ''
  });
  
  // SMS verification state
  const [verificationStep, setVerificationStep] = useState('sms'); // 'sms' or 'id'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [smsVerified, setSmsVerified] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  
  // Wizard steps
  const [waiverStep, setWaiverStep] = useState(1); // 1=read, 2=name, 3=signature, 4=verify, 5=review
  
  // Magic link auth state
  const [showAuthScreen, setShowAuthScreen] = useState(false);
  const [authEmail, setAuthEmail] = useState('');
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [accessSent, setAccessSent] = useState(false);
  const [magicLinkToken, setMagicLinkToken] = useState(null);
  const [showSeeMore, setShowSeeMore] = useState(false); // Toggle see more section with add-ons
  
  // Helper to format prices with currency
  const formatPrice = (amount, currency = 'USD') => {
    const symbols = { USD: '$', EUR: '€', GBP: '£', AED: 'AED ', INR: '₹', JPY: '¥' };
    const symbol = symbols[currency?.toUpperCase()] || `${currency} `;
    return `${symbol}${parseFloat(amount || 0).toFixed(2)}`;
  };

  useEffect(() => {
    // Check for magic link token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      // Has magic link token - try to use it
      setMagicLinkToken(token);
      loadBookingWithToken(token);
    } else {
      // No token - check for existing session
      const sessionToken = localStorage.getItem(`ticket_session_${ticketId}`);
      const sessionExp = localStorage.getItem(`ticket_session_exp_${ticketId}`);
      
      if (sessionToken && sessionExp && Date.now() < parseInt(sessionExp)) {
        // Valid session exists
        setMagicLinkToken(sessionToken);
        loadBookingWithToken(sessionToken);
      } else {
        // No valid session - show auth screen
        setShowAuthScreen(true);
        setLoading(false);
      }
    }
  }, [ticketId]);

  const loadBookingWithToken = async (token) => {
    try {
      // Use v2 PostgreSQL endpoint
      const response = await api.get(`/v2/public/bookings/${ticketId}?token=${token}`);
      
      // Response structure: { booking: {...}, service: {...}, organization: {...} }
      const tripData = response.data.booking;
      const serviceData = response.data.service;
      const orgData = response.data.organization;
      
      // Add organization settings to booking
      if (orgData) {
        tripData.waiversEnabled = orgData.waiversEnabled;
        tripData.customWaiverText = orgData.customWaiverText;
        tripData.waiverText = orgData.waiverText || orgData.customWaiverText;
        tripData.waiverVersion = orgData.waiverVersion || '1.0';  // Get current waiver version
        tripData.reviewLinkEnabled = orgData.reviewLinkEnabled;
        tripData.reviewLinkUrl = orgData.reviewLinkUrl;
        tripData.checkInEnabled = orgData.checkInEnabled;
      }
      
      // Enrich add-on data with titles from service (for bookings created before titles were stored)
      if (tripData.selectedAddOns && Array.isArray(tripData.selectedAddOns) && serviceData?.addOns) {
        try {
          tripData.selectedAddOns = tripData.selectedAddOns.map((addon, index) => {
            // Handle string add-ons (legacy format)
            if (typeof addon === 'string') {
              const serviceAddon = serviceData.addOns[index];
              return {
                id: `addon_${index}`,
                title: serviceAddon?.title || serviceAddon?.name || addon || `Add-on ${index + 1}`,
                price: serviceAddon?.price || 0
              };
            }
            
            // If addon already has a good title, use it
            if (addon.title && !addon.title.startsWith('addon_')) {
              return addon;
            }
            
            // Try to match by ID or index to get title from service
            const addonId = addon.id || `addon_${index}`;
            const serviceAddon = serviceData.addOns.find((sa, i) => 
              sa.id === addonId || `addon_${i}` === addonId
            ) || serviceData.addOns[index];
            
            if (serviceAddon) {
              return {
                ...addon,
                title: serviceAddon.title || serviceAddon.name || `Add-on ${index + 1}`,
                description: serviceAddon.description || addon.description || ''
              };
            }
            return addon;
          });
        } catch (enrichError) {
          console.error('Error enriching add-ons:', enrichError);
          // Keep original add-ons if enrichment fails
        }
      }
      
      setBooking(tripData);
      setService(serviceData); // Store service data
      
      // Set organization branding if available
      if (orgData) {
        setOrgBranding({
          logoUrl: orgData.logoUrl || '',
          primaryColor: orgData.primaryColor || '#007AFF',
          name: orgData.name || '',
          showPoweredByBookingFlows: orgData.showPoweredByBookingFlows !== false,
          companyWebsite: orgData.companyWebsite || '',
          // Conversion tracking
          googleAdsId: orgData.googleAdsId || null,
          googleAdsLabel: orgData.googleAdsLabel || null,
          metaPixelId: orgData.metaPixelId || null,
        });
      }
      
      // Store session token for 24 hours
      localStorage.setItem(`ticket_session_${ticketId}`, token);
      localStorage.setItem(`ticket_session_exp_${ticketId}`, Date.now() + (24 * 60 * 60 * 1000));
      
      setShowAuthScreen(false);
      setLoading(false);
    } catch (err) {
      console.error('Error loading booking:', err);
      
      if (err.response?.status === 401) {
        // Token expired or invalid
        setShowAuthScreen(true);
        setError('This link has expired or is invalid. Please request a new one below.');
        // Clear invalid session
        localStorage.removeItem(`ticket_session_${ticketId}`);
        localStorage.removeItem(`ticket_session_exp_${ticketId}`);
        // Clean up URL to remove invalid token
        window.history.replaceState({}, '', `/customer-ticket/${ticketId}`);
      } else {
        setError('Booking not found');
      }
      
      setLoading(false);
    }
  };
  
  const handleRequestAccess = async () => {
    if (!authEmail || !authEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setRequestingAccess(true);
    try {
      // Use v2 PostgreSQL endpoint
      await api.post(`/v2/public/bookings/${ticketId}/request-access`, { email: authEmail });
      setAccessSent(true);
      toast.success('Check your email for the ticket link!');
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Email does not match this booking');
      } else if (err.response?.status === 404) {
        toast.error('Booking not found');
      } else {
        toast.error('Failed to send link. Please try again.');
      }
    } finally {
      setRequestingAccess(false);
    }
  };

  // Auth method tabs
  const [authTab, setAuthTab] = useState('email'); // 'email' | 'phone'
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyPhone, setVerifyPhone] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState('');

  // Format confirmation code input: auto-insert BK- prefix and uppercase
  const formatCodeInput = (val) => {
    let v = val.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (v.startsWith('BK')) v = v.slice(2);
    return v ? 'BK-' + v.slice(0, 6) : '';
  };

  const handleVerifyPhone = async () => {
    setVerifyError('');
    const rawCode = verifyCode.replace(/[^A-Z0-9]/gi, '');
    if (rawCode.length < 5) { setVerifyError('Enter your confirmation code'); return; }
    const digits = verifyPhone.replace(/\D/g, '');
    if (digits.length < 7) { setVerifyError('Enter a valid phone number'); return; }
    setVerifying(true);
    try {
      const res = await api.post('/v2/public/bookings/verify-phone', {
        confirmationCode: verifyCode,
        phone: verifyPhone,
      });
      if (res.data.token) {
        setMagicLinkToken(res.data.token);
        // Store session
        localStorage.setItem(`ticket_session_${ticketId}`, res.data.token);
        localStorage.setItem(`ticket_session_exp_${ticketId}`, Date.now() + (24 * 60 * 60 * 1000));
        loadBookingWithToken(res.data.token);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Verification failed. Check your details and try again.';
      setVerifyError(msg);
    } finally {
      setVerifying(false);
    }
  };

  const formatTime = (time) => {
    if (!time) return 'N/A';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    // IMPORTANT: Parse date as local, NOT UTC (prevents timezone shifts)
    // Input: "2025-11-29" should display as Nov 29, not Nov 28
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day); // Month is 0-indexed
    
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${months[date.getMonth()]} ${date.getDate()}`;
  };

  const handleSignWaiver = async () => {
    if (!signature.trim()) {
      toast.error('Please type your full name');
      return;
    }
    
    if (!signaturePad || signaturePad.isEmpty()) {
      toast.error('Please draw your signature');
      return;
    }
    
    // Check verification: SMS OR ID upload
    if (!smsVerified && !idImage) {
      toast.error('Please verify your phone or upload ID');
      return;
    }

    setSigningWaiver(true);
    try {
      // Get device fingerprint
      const deviceInfo = {};
      const safeGet = (fn, fallback = 'Unknown') => {
        try { return fn(); } catch { return fallback; }
      };
      
      deviceInfo.userAgent = safeGet(() => navigator.userAgent);
      deviceInfo.platform = safeGet(() => navigator.platform);
      deviceInfo.language = safeGet(() => navigator.language);
      deviceInfo.languages = safeGet(() => navigator.languages?.join(', '));
      deviceInfo.screenResolution = safeGet(() => `${window.screen.width}x${window.screen.height}`);
      deviceInfo.screenColorDepth = safeGet(() => window.screen.colorDepth);
      deviceInfo.screenPixelDepth = safeGet(() => window.screen.pixelDepth);
      deviceInfo.windowSize = safeGet(() => `${window.innerWidth}x${window.innerHeight}`);
      deviceInfo.timezone = safeGet(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
      deviceInfo.timezoneOffset = safeGet(() => new Date().getTimezoneOffset());
      deviceInfo.cookiesEnabled = safeGet(() => navigator.cookieEnabled);
      deviceInfo.doNotTrack = safeGet(() => navigator.doNotTrack);
      deviceInfo.hardwareConcurrency = safeGet(() => navigator.hardwareConcurrency);
      deviceInfo.maxTouchPoints = safeGet(() => navigator.maxTouchPoints);
      deviceInfo.onLine = safeGet(() => navigator.onLine);
      deviceInfo.vendor = safeGet(() => navigator.vendor);
      deviceInfo.appName = safeGet(() => navigator.appName);
      deviceInfo.appVersion = safeGet(() => navigator.appVersion);
      deviceInfo.oscpu = safeGet(() => navigator.oscpu);
      deviceInfo.productSub = safeGet(() => navigator.productSub);
      deviceInfo.deviceMemory = safeGet(() => navigator.deviceMemory);
      deviceInfo.connection = safeGet(() => navigator.connection ? {
        effectiveType: navigator.connection.effectiveType,
        downlink: navigator.connection.downlink,
        rtt: navigator.connection.rtt,
        saveData: navigator.connection.saveData
      } : null);
      
      // Get compressed signature (15-20KB instead of 100KB)
      const compressedSignature = getCompressedSignature();
      
      // Generate PIN
      const tripIdDigits = (trip.confirmationCode || trip.id).replace(/[^0-9]/g, '');
      const pin = tripIdDigits.slice(-3).padStart(3, '0');
      
      const payload = {
        signature: signature.trim(),
        signatureImage: '', // Skip signature image for now - can cause issues
        smsVerified: smsVerified,
        phoneNumber: phoneNumber,
        idImage: '', // Skip ID for now
        deviceInfo: deviceInfo,
        pin: pin,
        signedAt: new Date().toISOString()
      };
      
      // Use v2 PostgreSQL endpoint
      await api.post(`/v2/public/bookings/${ticketId}/sign-waiver`, payload);
      
      toast.success(`Waiver signed! Your PIN is: ${pin}`);
      setShowWaiverModal(false);
      
      // Reset all state
      setSignature('');
      setIdImage(null);
      setIdPreview(null);
      setPhoneNumber('');
      setSmsCode('');
      setCodeSent(false);
      setSmsVerified(false);
      setVerificationStep('sms');
      if (signaturePad) signaturePad.clear();
      
      window.location.reload();
    } catch (error) {
      console.error('Error signing waiver:', error);
      
      // Provide more specific error messages
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail || '';
        
        if (status === 404) {
          toast.error('Booking not found. Please refresh the page and try again.');
        } else if (status === 403) {
          toast.error('Access denied. Please refresh the page and try again.');
        } else if (status === 400) {
          toast.error(`Invalid request: ${detail || 'Please check your information.'}`);
        } else {
          toast.error(`Failed to sign waiver (Error ${status}). Please try again.`);
        }
      } else if (error.request) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        toast.error('Failed to sign waiver. Please try again.');
      }
    } finally {
      setSigningWaiver(false);
    }
  };

  const handleIdUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Please upload an image or PDF');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum 5MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setIdImage(base64);
      setIdPreview(base64);
      toast.success('ID uploaded!');
    };
    reader.readAsDataURL(file);
  };

  // Format phone number for Twilio (E.164)
  const formatPhoneForTwilio = (input) => {
    const digits = input.replace(/\D/g, ''); // Remove all non-digits
    
    // Already has + prefix, use as-is
    if (input.startsWith('+')) {
      return input.replace(/\D/g, '').length >= 10 ? input : null;
    }
    
    // Check for known country code patterns
    for (const country of COUNTRIES) {
      const codeDigits = country.phoneCode.replace('+', '');
      // Match expected digit length for this country
      if (digits.length === country.phoneDigits) {
        return `${country.phoneCode}${digits}`;
      }
      // Match with country code prefix
      if (digits.startsWith(codeDigits) && digits.length === codeDigits.length + country.phoneDigits) {
        return `+${digits}`;
      }
    }
    
    // US number fallback (10 digits)
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    
    // International with country code (11+ digits)
    if (digits.length >= 11) {
      return `+${digits}`;
    }
    
    return null; // Invalid
  };

  // Compress signature to small JPEG
  const getCompressedSignature = () => {
    if (!signaturePad || signaturePad.isEmpty()) return null;
    
    const canvas = signaturePad.getTrimmedCanvas();
    // Compress to JPEG at 30% quality (much smaller than PNG)
    return canvas.toDataURL('image/jpeg', 0.3);
  };

  const handleSendSMS = async () => {
    const formatted = formatPhoneForTwilio(phoneNumber);
    
    if (!formatted) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setSendingCode(true);
    try {
      await api.post('/public/sms/send-code', { phoneNumber: formatted });
      
      setPhoneNumber(formatted); // Save formatted version
      setCodeSent(true);
      toast.success('Code sent! Check your phone.');
    } catch (error) {
      console.error('Error sending SMS:', error);
      toast.error('Failed to send code. Try ID upload instead.');
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifySMS = async () => {
    if (!smsCode || smsCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setVerifyingCode(true);
    try {
      const response = await api.post('/public/sms/verify-code', {
        phoneNumber: phoneNumber, // Already formatted
        code: smsCode
      });
      
      if (response.data.valid) {
        setSmsVerified(true);
        toast.success('Phone verified! ✓');
      } else {
        toast.error('Invalid code. Please try again.');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      toast.error('Verification failed. Try again or use ID upload.');
    } finally {
      setVerifyingCode(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === 'assigned') return { bg: 'bg-green-500', text: 'Confirmed', color: '#22c55e' };
    if (status === 'partial') return { bg: 'bg-amber-500', text: 'Pending', color: '#f59e0b' };
    return { bg: 'bg-red-400', text: 'Unconfirmed', color: '#ef4444' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading ticket...</p>
        </div>
      </div>
    );
  }

  // Show auth screen if no valid session/token
  if (showAuthScreen) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl border-0">
          <CardContent className="p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Access Your Ticket</h2>
              {error && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">{error}</p>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="flex rounded-xl bg-gray-100 p-1 mb-6">
              <button
                onClick={() => { setAuthTab('email'); setVerifyError(''); }}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${authTab === 'email' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >
                📧 Email
              </button>
              <button
                onClick={() => { setAuthTab('phone'); setVerifyError(''); }}
                className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${authTab === 'phone' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
              >
                📱 Conf Code + Phone
              </button>
            </div>

            {/* EMAIL TAB */}
            {authTab === 'email' && (
              <div className="space-y-4">
                {accessSent ? (
                  <div>
                    <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-center">
                      <div className="flex items-center justify-center gap-2 text-green-700 font-medium mb-1">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                        Check your email!
                      </div>
                      <p className="text-sm text-green-600">A secure link has been sent to your inbox.</p>
                    </div>
                    <button onClick={() => setAccessSent(false)} className="w-full text-center text-blue-600 text-sm font-medium hover:underline mt-3">
                      Didn't receive it? Send again
                    </button>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-500 text-center -mt-2 mb-4">We'll email you a secure link to view your ticket.</p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                      <Input type="email" placeholder="your@email.com" value={authEmail}
                        onChange={e => setAuthEmail(e.target.value)}
                        onKeyPress={e => e.key === 'Enter' && handleRequestAccess()}
                        className="text-base h-12 border-gray-200 focus:border-blue-500" autoFocus />
                    </div>
                    <Button onClick={handleRequestAccess} disabled={!authEmail.includes('@') || requestingAccess}
                      className="w-full h-12 text-base font-semibold" style={{ backgroundColor: '#007AFF' }}>
                      {requestingAccess ? 'Sending...' : (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                          Email Me My Ticket
                        </span>
                      )}
                    </Button>
                    <p className="text-xs text-gray-400 text-center">Link expires in 24 hours for security.</p>
                  </>
                )}
              </div>
            )}

            {/* PHONE TAB */}
            {authTab === 'phone' && (
              <div className="space-y-4">
                <p className="text-sm text-gray-500 text-center -mt-2 mb-4">
                  Enter your booking confirmation code and the phone number you used when booking.
                </p>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Confirmation Code</label>
                  <Input
                    placeholder="BK-XXXXXX"
                    value={verifyCode}
                    onChange={e => setVerifyCode(formatCodeInput(e.target.value))}
                    className="text-base h-12 border-gray-200 focus:border-blue-500 font-mono tracking-wider uppercase"
                    maxLength={9}
                    autoFocus
                  />
                  <p className="text-xs text-gray-400 mt-1">e.g. BK-IXFJ6Z — from your booking confirmation SMS or email</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                  <Input
                    type="tel"
                    placeholder="(303) 717-5022 or +13037175022"
                    value={verifyPhone}
                    onChange={e => setVerifyPhone(e.target.value.replace(/[^0-9+()\-\s]/g, '').slice(0, 20))}
                    maxLength={20}
                    onKeyPress={e => e.key === 'Enter' && handleVerifyPhone()}
                    className="text-base h-12 border-gray-200 focus:border-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">Any format works — we'll match it automatically</p>
                </div>

                {verifyError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-700">{verifyError}</p>
                  </div>
                )}

                <Button
                  onClick={handleVerifyPhone}
                  disabled={verifying || verifyCode.length < 5 || verifyPhone.replace(/\D/g, '').length < 7}
                  className="w-full h-12 text-base font-semibold" style={{ backgroundColor: '#007AFF' }}
                >
                  {verifying ? 'Verifying...' : (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                      View My Ticket
                    </span>
                  )}
                </Button>
                <p className="text-xs text-gray-400 text-center">
                  No country code needed for US numbers. 5 attempts max for security.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !trip) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-900 mb-2">Booking Not Found</h2>
            <p className="text-red-700">The trip ticket you're looking for doesn't exist or has been deleted.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusColor(trip.status);
  const waiversEnabled = trip.waiversEnabled || false; // From backend
  const hasAnySignatures = trip.waiverSignatures && trip.waiverSignatures.length > 0;
  const showDetails = !waiversEnabled || hasAnySignatures; // Show if waivers off OR someone signed

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-8 px-4">
      {/* Conversion Tracking - fires purchase event on ticket view */}
      <ConversionTracking booking={trip} organization={orgBranding} eventType="purchase" />
      
      <div className="max-w-md mx-auto">

        <div className="bg-white rounded-2xl shadow-2xl border-l-4 p-6" style={{ borderLeftColor: orgBranding.primaryColor }}>

          {/* ── Card header: logo centered → confirmed + ref justify-between ── */}
          <div className="mb-5 pb-5 border-b border-gray-100">
            {/* Logo — centered, with a little breathing room below */}
            <div className="flex justify-center mb-4">
              {orgBranding.logoUrl ? (
                <img
                  src={orgBranding.logoUrl}
                  alt={orgBranding.name}
                  className="h-7 w-auto max-w-[120px] object-contain"
                />
              ) : (
                <span className="text-sm font-semibold" style={{ color: orgBranding.primaryColor }}>
                  {orgBranding.name}
                </span>
              )}
            </div>

            {/* Ref ID left (blue) ←→ Confirmed badge right (blue) */}
            <div className="flex items-center justify-between">
              <p className="text-xs font-mono font-semibold" style={{ color: orgBranding.primaryColor }}>
                {trip.confirmationCode || trip.id.substring(0, 8).toUpperCase()}
              </p>
              {trip.checkInStatus === 'checked_in' ? (
                <span className="text-xs bg-purple-100 text-purple-800 px-3 py-1 rounded-full font-semibold flex items-center gap-1.5">
                  <Check className="w-3 h-3" />
                  Checked In {trip.checkInAt && new Date(trip.checkInAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                </span>
              ) : (
                <span className="text-xs px-3 py-1 rounded-full font-semibold" style={{ backgroundColor: `${orgBranding.primaryColor}18`, color: orgBranding.primaryColor }}>Confirmed</span>
              )}
            </div>
          </div>

          {/* Service Name — clean, no "by" subtitle (company already in header) */}
          <div className="mb-2">
            <h3 className="font-bold text-xl text-gray-900 uppercase">
              {service?.name || trip.serviceName || 'Loading...'}
            </h3>
          </div>
          
          {/* Customer Name & Phone - Side by Side */}
          {trip.customerName && (
            <div className="mb-4 pb-4 border-b border-gray-200">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer</p>
                  <p className="font-semibold text-gray-900">
                    {(() => {
                      const names = trip.customerName.split(' ');
                      const firstName = names[0];
                      const lastInitial = names[names.length - 1]?.[0] || '';
                      return `${firstName} ${lastInitial}.`;
                    })()}
                  </p>
                </div>
                {trip.customerPhone && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Phone</p>
                    <p className="font-semibold text-gray-900">
                      {formatPhoneNumber(trip.customerPhone)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Booking Details */}
          <div className="space-y-3 mb-6">
            {/* Asset | Team */}
            <div className="grid grid-cols-2 gap-4">
              {trip.assetName && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Asset</p>
                  <p className="font-semibold text-gray-900">{trip.assetName}</p>
                </div>
              )}
              
              {trip.teamAssignments && trip.teamAssignments.filter(c => c.teamId).length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Team</p>
                  <p className="font-semibold text-gray-900">
                    {trip.teamAssignments.filter(c => c.teamId).map(c => {
                      const names = c.teamName.split(' ');
                      const firstName = names[0];
                      const lastInitial = names[names.length - 1]?.[0] || '';
                      return `${firstName} ${lastInitial}.`;
                    }).join(', ')}
                  </p>
                </div>
              )}
            </div>
            
            {/* Date | Time — branches for flexible stay (check-in/check-out) vs slot-based */}
            {(trip.checkOutDate || trip.check_out_date) ? (
              /* ── Flexible stay: show check-in → check-out ── */
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Check-In</p>
                    <p className="font-semibold text-gray-900">{formatDate(trip.preferredDate || trip.bookingDate || trip.date)}</p>
                    <p className="text-sm text-gray-600">{formatTime(trip.preferredTime || trip.startTime)} {trip.timezone ? getTimezoneAbbr(trip.timezone) : ''}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Check-Out</p>
                    <p className="font-semibold text-gray-900">{formatDate(trip.checkOutDate || trip.check_out_date)}</p>
                    <p className="text-sm text-gray-600">
                      {trip.checkOutTime || (service?.checkOutTime) || '11:00 AM'} {trip.timezone ? getTimezoneAbbr(trip.timezone) : ''}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Stay</p>
                    <p className="font-semibold text-gray-900">
                      {(trip.numNights || trip.num_nights) ? `${trip.numNights || trip.num_nights} night${(trip.numNights || trip.num_nights) !== 1 ? 's' : ''}` : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guests</p>
                    <p className="font-semibold text-gray-900">{trip.groupSize || trip.partySize || 1} {(trip.groupSize || trip.partySize || 1) === 1 ? 'person' : 'people'}</p>
                  </div>
                </div>
              </>
            ) : (
              /* ── Slot-based: original date + time + duration display ── */
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Date</p>
                    <p className="font-semibold text-gray-900">{formatDate(trip.preferredDate || trip.date)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Time</p>
                    <p className="font-semibold text-gray-900">
                      {formatTime(trip.preferredTime || trip.startTime)} {trip.timezone ? getTimezoneAbbr(trip.timezone) : ''}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Duration</p>
                    <p className="font-semibold text-gray-900">
                      {formatDuration(trip.durationMinutes || trip.duration || service?.duration)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Guests</p>
                    <p className="font-semibold text-gray-900">{trip.groupSize || trip.partySize || trip.participantNames?.length || 1} {(trip.groupSize || trip.partySize || trip.participantNames?.length || 1) === 1 ? 'person' : 'people'}</p>
                  </div>
                </div>
              </>
            )}
            
            {/* Location Section - City/Country and Meeting Point */}
            {(trip.pickupLocation || trip.city || service?.locationDetails || service?.city) && (
              <div className="grid grid-cols-2 gap-4">
                {/* City/Country */}
                {(trip.city || service?.city) && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Location</p>
                    <p className="font-semibold text-gray-900">
                      {trip.city || service?.city}{(trip.country || service?.country) ? `, ${trip.country || service?.country}` : ''}
                    </p>
                  </div>
                )}
                {/* Meeting Point */}
                {(trip.pickupLocation || service?.locationDetails) && (
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Meeting Point</p>
                    <p className="font-semibold text-gray-900">
                      {trip.pickupLocation || service?.locationDetails}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Who's Signed the Waiver - Green/Orange Box */}
          {waiversEnabled && trip.waiverSignatures && trip.waiverSignatures.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <p className="text-xs font-semibold text-green-900 mb-2 uppercase tracking-wide">Who's Signed the Waiver</p>
              <div className="space-y-1">
                {(() => {
                  const MAX_VISIBLE = 5;
                  const totalSignatures = trip.waiverSignatures.length;
                  const visibleSignatures = showAllSignatures 
                    ? trip.waiverSignatures 
                    : trip.waiverSignatures.slice(0, MAX_VISIBLE);
                  const hiddenCount = totalSignatures - MAX_VISIBLE;
                  
                  return (
                    <>
                      {visibleSignatures.map((sig, idx) => {
                        // Camera icon ONLY for team member ID checks (in-person verification)
                        const needsIdCheck = sig.verificationMethod === 'team_member_id_check';
                        
                        return (
                          <div key={idx} className="flex items-center gap-2">
                            {needsIdCheck ? (
                              <>
                                <svg className="w-4 h-4 text-orange-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                <span className="text-sm text-orange-700 font-medium">{sig.signatureName}</span>
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                                <span className="text-sm text-green-800">{sig.signatureName}</span>
                              </>
                            )}
                          </div>
                        );
                      })}
                      
                      {/* Show More/Less button for large groups */}
                      {totalSignatures > MAX_VISIBLE && (
                        <button
                          onClick={() => setShowAllSignatures(!showAllSignatures)}
                          className="flex items-center gap-1 text-xs text-green-700 hover:text-green-900 font-medium mt-2 transition-colors"
                        >
                          {showAllSignatures ? (
                            <>
                              <ChevronUp className="w-3 h-3" />
                              Show Less
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-3 h-3" />
                              See {hiddenCount} More
                            </>
                          )}
                        </button>
                      )}
                    </>
                  );
                })()}
                
                {(trip.groupSize || trip.partySize) && trip.waiverSignatures.length < (trip.groupSize || trip.partySize) && (
                  <div className="flex items-center gap-2 opacity-50 mt-2 pt-2 border-t border-green-200">
                    <div className="w-4 h-4 border-2 border-gray-400 rounded flex-shrink-0"></div>
                    <span className="text-sm text-gray-600">
                      {trip.waiverSignatures.length}/{trip.groupSize || trip.partySize} expected guests signed
                    </span>
                  </div>
                )}
                {(trip.groupSize || trip.partySize) && trip.waiverSignatures.length >= (trip.groupSize || trip.partySize) && (
                  <div className="flex items-center gap-2 mt-2 pt-2 border-t border-green-200">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-800 font-medium">
                      {trip.waiverSignatures.length} {trip.waiverSignatures.length === 1 ? 'guest has' : 'guests have'} signed
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Payment section — adapts to payment status ── */}
          {(() => {
            const paymentDone = ['paid', 'refunded', 'partial_refund', 'completed'].includes(trip.paymentStatus);
            const isDepositPaid = trip.paymentStatus === 'deposit_paid';
            const grandTotal = trip.totalAmount || trip.totalPrice || trip.servicePrice || 0;
            const paidSoFar = trip.paidAmount || trip.amountPaid || trip.depositPaid || 0;
            const remaining = Math.max(0, grandTotal - paidSoFar);

            if (paymentDone) return null;

            const handlePay = async () => {
              try {
                const response = await api.post(`/public/bookings/${ticketId}/generate-payment-checkout`);
                if (response.data.checkoutUrl) {
                  window.location.href = response.data.checkoutUrl;
                } else {
                  toast.error('Failed to create payment link');
                }
              } catch (error) {
                toast.error(error.response?.data?.detail || 'Failed to create payment link');
              }
            };

            if (isDepositPaid && remaining > 0.05) {
              // Deposit paid — show clear balance-due card
              return (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-semibold text-blue-900">Deposit paid</span>
                    <span className="text-sm font-semibold text-blue-700">{formatPrice(paidSoFar, trip.currency)}</span>
                  </div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-amber-700 font-semibold">Balance due at check-in</span>
                    <span className="text-sm font-bold text-amber-700">{formatPrice(remaining, trip.currency)}</span>
                  </div>
                  <Button
                    onClick={handlePay}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                    data-testid="pay-balance-btn"
                  >
                    Pay Remaining Balance — {formatPrice(remaining, trip.currency)}
                  </Button>
                </div>
              );
            }

            if (remaining > 0) {
              // Standard unpaid / pending booking
              return (
                <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-2">
                  <p className="text-sm text-amber-800 font-semibold text-center mb-3">
                    Payment required to confirm your booking
                  </p>
                  <Button
                    onClick={handlePay}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold"
                    data-testid="complete-payment-btn"
                  >
                    Complete Payment — {formatPrice(remaining, trip.currency)}
                  </Button>
                </div>
              );
            }

            return null;
          })()}

          {/* Level 1: Sign Waiver — always full width when active */}
          {/* Level 2: Review + QR split side-by-side if both active, full-width if only one */}
          {(() => {
            // DATE RULE: booking_date is YYYY-MM-DD — parse as local midnight (see dateUtils contract)
            const bookingDateLocal = trip.bookingDate || trip.date
              ? new Date((trip.bookingDate || trip.date) + 'T00:00:00')
              : null;
            const now = new Date();
            const daysSinceBooking = bookingDateLocal
              ? Math.floor((now - bookingDateLocal) / (1000 * 60 * 60 * 24))
              : -999; // negative = future booking, always show CTAs

            const waiverExpired = daysSinceBooking > 7;
            const qrExpired = daysSinceBooking > 1;
            const reviewExpired = daysSinceBooking > 90;

            const showWaiver = waiversEnabled && !waiverExpired;
            const showReview = trip.reviewLinkEnabled && trip.reviewLinkUrl && !reviewExpired;
            const showQR = trip.checkInEnabled && trip.checkInStatus !== 'checked_in' && !qrExpired;
            const both = showReview && showQR;

            if (!showWaiver && !showReview && !showQR) return null;

            return (
              <div className="space-y-2 pt-4 pb-4">
                {/* Sign Waiver — full width, Level 1 */}
                {showWaiver && (() => {
                  const signatureCount = trip.waiverSignatures?.length || 0;
                  const expectedGuests = trip.groupSize || trip.partySize || 10;
                  const allExpectedSigned = signatureCount >= expectedGuests;
                  return (
                    <button
                      onClick={() => setShowWaiverModal(true)}
                      className="w-full text-white py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 relative hover:opacity-90"
                      style={{ background: orgBranding.primaryColor }}
                    >
                      <Lock className="w-4 h-4" />
                      <span>Sign Waiver</span>
                      {signatureCount > 0 && (
                        <span className="absolute right-4 text-xs opacity-75">
                          {signatureCount} signed{allExpectedSigned ? ' ✓' : ''}
                        </span>
                      )}
                    </button>
                  );
                })()}

                {/* Level 2: QR + Review */}
                {(showQR || showReview) && (
                  <div className={both ? 'grid grid-cols-2 gap-2' : ''}>
                    {showQR && (
                      <button
                        onClick={() => setShowQRCode(!showQRCode)}
                        className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 shadow-lg transition-all"
                      >
                        <QrCode className="w-4 h-4" />
                        {both ? 'QR Code' : (showQRCode ? 'Hide QR Code' : 'Show QR Code')}
                      </button>
                    )}
                    {showReview && (
                      <a
                        href={trip.reviewLinkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 shadow-lg transition-all block text-center"
                      >
                        <Star className="w-4 h-4 fill-white inline-block mr-1" />
                        {both ? 'Review' : 'Leave a Review'}
                      </a>
                    )}
                  </div>
                )}

                {/* QR Code Display */}
                {showQR && showQRCode && (
                  <div className="bg-white border-2 border-purple-200 rounded-lg p-6 text-center">
                    <div className="inline-block bg-white p-3 rounded-lg border-2 border-gray-300">
                      <QRCode
                        value={`${window.location.origin}/check-in/${trip.confirmationCode}?org=${trip.organizationId}`}
                        size={200}
                        level="M"
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-3">Staff will scan this code to check you in</p>
                  </div>
                )}
              </div>
            );
          })()}
          
          {/* Add-ons + Custom Questions — shown when either exists */}
          {(() => {
            const hasAddOns = trip.selectedAddOns && trip.selectedAddOns.length > 0;
            const hasAnswers = trip.customAnswers && trip.customAnswers.length > 0 &&
              trip.customAnswers.some(r => r && (r.answer !== undefined && r.answer !== null && r.answer !== ''));
            if (!hasAddOns && !hasAnswers) return null;

            const btnLabel = hasAddOns && hasAnswers
              ? 'See Add-ons & Questions'
              : hasAddOns ? 'See Add-ons' : 'See Questions';
            const hideLabel = hasAddOns && hasAnswers
              ? 'Hide Add-ons & Questions'
              : hasAddOns ? 'Hide Add-ons' : 'Hide Questions';

            return (
              <div className="mt-5 pt-5 border-t border-gray-100">
                <button
                  onClick={() => setShowSeeMore(!showSeeMore)}
                  className="w-full text-center text-sm text-gray-500 hover:text-blue-600 transition-colors inline-flex items-center justify-center gap-2 py-1.5"
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${showSeeMore ? 'rotate-180' : ''}`} />
                  {showSeeMore ? hideLabel : btnLabel}
                </button>

                {showSeeMore && (
                  <div className="mt-3 space-y-4 pt-3">

                    {/* ── Add-ons ───────────────────────────────────── */}
                    {hasAddOns && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Add-ons</p>
                        <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                          {trip.selectedAddOns.map((addon, index) => {
                            const addonName = typeof addon === 'string'
                              ? addon
                              : (addon.title || addon.name || addon.id || 'Add-on');
                            const addonPrice = typeof addon === 'object' ? addon.price : null;
                            const addonQty   = typeof addon === 'object' && addon.quantity > 1 ? addon.quantity : null;
                            return (
                              <div key={index} className="flex justify-between items-center text-sm">
                                <span className="text-gray-700">
                                  {addonQty && <span className="font-medium text-gray-500 mr-1">{addonQty}×</span>}
                                  {addonName}
                                </span>
                                {addonPrice != null && (
                                  <span className="text-gray-600">{formatPrice(addonPrice, trip.currency)}</span>
                                )}
                              </div>
                            );
                          })}
                          {trip.addOnsTotal > 0 && (
                            <div className="flex justify-between items-center text-sm pt-2 border-t border-gray-200 mt-2">
                              <span className="font-medium text-gray-700">Add-ons Total</span>
                              <span className="font-semibold text-gray-900">{formatPrice(trip.addOnsTotal, trip.currency)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* ── Custom Questions / Answers ────────────────── */}
                    {hasAnswers && (
                      <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Your Responses</p>
                        <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                          {trip.customAnswers
                            .filter(r => r && r.answer !== undefined && r.answer !== null && r.answer !== '')
                            .map((resp, idx) => (
                              <div key={idx} className="text-sm">
                                <p className="text-gray-500 text-xs mb-0.5">{resp.question || `Question ${idx + 1}`}</p>
                                <p className="text-gray-800 font-medium">{String(resp.answer)}</p>
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    )}

                    {/* ── Multi-currency notice ─────────────────────── */}
                    {trip.isMultiCurrency && trip.presentmentCurrency && (
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="text-sm text-blue-700">
                          You paid in <span className="font-semibold">{trip.presentmentCurrency.toUpperCase()}</span>
                        </p>
                      </div>
                    )}

                    {/* ── Emergency Contact ─────────────────────────── */}
                    {(() => {
                      const emergencyNum = trip?.emergencyContactNumber || trip?.serviceEmergencyNumber;
                      const placeholders = ['555-555-5555', '+1-555-555-5555', '(555) 555-5555', '+1 (555) 555-5555', '5555555555', '1231231234', '(512) 555-5555', '+1-555-0100'];
                      const isValid = emergencyNum && !placeholders.some(p => emergencyNum.includes(p.replace(/\D/g, '')));
                      return isValid ? (
                        <button
                          onClick={() => setShowEmergencyModal(true)}
                          className="w-full text-sm text-gray-600 hover:text-orange-600 transition-colors inline-flex items-center justify-center gap-2 py-2 border border-gray-200 rounded-lg hover:border-orange-200 hover:bg-orange-50"
                        >
                          <AlertTriangle className="w-4 h-4" />
                          Show Emergency Contact
                        </button>
                      ) : null;
                    })()}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Powered by BookingFlows — inside the card at the bottom */}
          <div className="text-center mt-5 pt-4 border-t border-gray-100">
            <a
              href={orgBranding.showPoweredByBookingFlows ? 'https://bookingflows.com' : (orgBranding.companyWebsite || '#')}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors inline-flex items-center gap-1"
            >
              {orgBranding.showPoweredByBookingFlows ? (
                <>Powered by <span className="font-semibold" style={{ color: orgBranding.primaryColor }}>BookingFlows</span></>
              ) : (
                <>© <span className="font-semibold" style={{ color: orgBranding.primaryColor }}>{orgBranding.name}</span></>
              )}
            </a>
          </div>
        </div>

        {/* ── Action row: Apple Wallet + Save to Calendar ── */}
        {(trip.paymentStatus === 'paid' || trip.paymentStatus === 'partial' || trip.status === 'confirmed') && (
          <div className="mt-8 flex items-center justify-center gap-3">
            <a
              href={`${API_URL}/v2/public/bookings/${ticketId}/apple-wallet?token=${ticketToken}`}
              download={`${ticketId}.pkpass`}
              data-testid="apple-wallet-btn"
              className="flex items-center gap-2 bg-black text-white px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-900 transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              Add to Wallet
            </a>
            <button
              onClick={() => {
                const confirmationCode = trip?.confirmationCode || ticketId;
                window.location.href = `${API_URL}/v2/public/bookings/${confirmationCode}/calendar.ics`;
                toast.success('Downloading calendar file...', { duration: 3000 });
              }}
              className="flex items-center gap-2 bg-black hover:bg-gray-900 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
              data-testid="save-to-calendar-btn"
            >
              <CalendarDays className="w-4 h-4" />
              Save to Calendar
            </button>
          </div>
        )}
        
        {/* Waiver Signing Modal */}
        {showWaiverModal && (
          <MobileWaiverWizard
            trip={trip}
            onClose={() => setShowWaiverModal(false)}
            organizationName={orgBranding.name}
            orgBranding={orgBranding}
            customWaiverText={trip.waiverText || trip.customWaiverText}
            brandColor={orgBranding.primaryColor}
            waiverVersion={trip.waiverVersion || '1.0'}
            trackingConfig={{
              googleAdsId: orgBranding.googleAdsId,
              googleAdsLabel: orgBranding.googleAdsLabel,
              metaPixelId: orgBranding.metaPixelId
            }}
            onSuccess={() => {
              setShowWaiverModal(false);
              // Reload booking data to reflect waiver completion
              const token = new URLSearchParams(window.location.search).get('token') || sessionStorage.getItem('ticketToken');
              if (token) loadBookingWithToken(token);
            }}
          />
        )}
        
        {/* Emergency Contact Modal */}
        {showEmergencyModal && (
          <Dialog open={showEmergencyModal} onOpenChange={setShowEmergencyModal}>
            <DialogContent className="max-w-md">
              {!emergencyAgreed ? (
                <>
                  <DialogHeader>
                    <div className="flex items-center justify-center mb-4">
                      <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                        <AlertTriangle className="w-8 h-8 text-orange-600" />
                      </div>
                    </div>
                    <DialogTitle className="text-center text-xl">Emergency Contact</DialogTitle>
                    <DialogDescription className="text-center mt-2">
                      This number is for <strong>emergencies only</strong>
                    </DialogDescription>
                  </DialogHeader>
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg my-4">
                    <p className="text-sm text-orange-900 text-center">
                      Emergency contacts are reserved for serious situations requiring immediate assistance. 
                      <strong className="block mt-2">Misuse of this number may result in service fees or restrictions.</strong>
                    </p>
                  </div>
                  <Button 
                    onClick={() => setEmergencyAgreed(true)}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    I Understand - Show Number
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => setShowEmergencyModal(false)}
                    className="w-full mt-2 text-gray-500"
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <DialogHeader>
                    <div className="flex items-center justify-center mb-4">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                        <Phone className="w-8 h-8 text-green-600" />
                      </div>
                    </div>
                    <DialogTitle className="text-center text-xl">Emergency Contact</DialogTitle>
                  </DialogHeader>
                  <div className="text-center py-4">
                    <a 
                      href={`tel:${trip?.emergencyContactNumber || trip?.serviceEmergencyNumber}`}
                      className="text-2xl font-bold text-gray-900 hover:text-blue-600 transition-colors"
                    >
                      {trip?.emergencyContactNumber || trip?.serviceEmergencyNumber}
                    </a>
                    <p className="text-sm text-gray-500 mt-2">Tap to call</p>
                  </div>
                  <Button 
                    onClick={() => {
                      setShowEmergencyModal(false);
                      setEmergencyAgreed(false);
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    Close
                  </Button>
                </>
              )}
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default CustomerTicketPage;
